"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ShareIcon,
  HeartIcon,
  TicketIcon,
  BuildingStorefrontIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import type { RemoteEvent } from "@/types";

type EventMobileViewProps = {
  event: RemoteEvent;
  imageUrl: string;
  dateInfo: {
    dateLabel: string;
    hourLabel: string;
    fullLabel: string;
  };
  location: string;
  address: string | null;
};

export default function EventMobileView({
  event,
  imageUrl,
  dateInfo,
  location,
  address,
}: EventMobileViewProps) {
  const [isFavorite, setIsFavorite] = useState(false);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text:
            event.description || `Confira ${event.title} no Santa Fé Conecta`,
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    }
  };

  // Parse date for badge - dateLabel format: "27 de nov" or "27 nov"
  const dateParts = dateInfo.dateLabel.split(" ");
  const day = dateParts[0] || "";
  // Se tem "de" no meio (ex: "27 de nov"), pega o último elemento, senão pega o segundo
  const month = dateParts.length === 3 ? dateParts[2] : dateParts[1] || "";

  return (
    <div className="md:hidden bg-gray-50 pb-24">
      {/* Hero Section */}
      <div className="relative">
        {/* Background Image */}
        <div className="relative h-80 w-full overflow-hidden">
          <Image
            src={imageUrl}
            alt={event.title}
            fill
            className="object-cover"
            priority
            unoptimized
          />
          <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/30 to-gray-50" />
        </div>

        {/* Top Navigation Bar */}
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="flex items-center justify-between p-4 pt-safe">
            <Link
              href="/eventos"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
              >
                {isFavorite ? (
                  <HeartSolidIcon className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartIcon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
              >
                <ShareIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Date Badge - Floating */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-10 z-10">
          <div className="bg-white rounded-2xl shadow-2xl ring-4 ring-white overflow-hidden">
            <div className="px-6 py-4 text-center">
              <div className="text-4xl font-bold text-gray-900">{day}</div>
              <div className="text-sm font-semibold text-mygreen uppercase tracking-wider">
                {month}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="pt-14 px-4 space-y-4">
        {/* Event Title & Status */}
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 bg-mygreen/10 text-mygreen rounded-full text-sm font-medium">
            <TicketIcon className="w-4 h-4" />
            Evento Confirmado
          </div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">
            {event.title}
          </h1>
        </div>

        {/* Quick Info Cards */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <ClockIcon className="w-6 h-6" />
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                  Horário
                </span>
                <p className="text-gray-900 font-semibold">
                  {dateInfo.hourLabel}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <MapPinIcon className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                  Local
                </span>
                <p className="text-gray-900 font-semibold">{location}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Full Date Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-mygreen/10 text-mygreen flex items-center justify-center">
              <CalendarIcon className="w-6 h-6" />
            </div>
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Data Completa
              </span>
              <p className="text-gray-900 font-semibold capitalize">
                {dateInfo.fullLabel}
              </p>
            </div>
          </div>
        </div>

        {/* Address Card */}
        {address && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-sm">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <BuildingStorefrontIcon className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                    Endereço
                  </span>
                  <p className="text-gray-900 font-medium leading-relaxed">
                    {address}
                  </p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="w-full aspect-video relative">
              <iframe
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${
                  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                  "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
                }&q=${encodeURIComponent(address)}`}
              />
            </div>
            <div className="p-4">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  address
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-mygreen text-white py-3 rounded-xl font-semibold hover:bg-mygreen/90 transition-colors"
              >
                <MapPinIcon className="w-5 h-5" />
                Como Chegar
              </a>
            </div>
          </div>
        )}

        {/* Description Card */}
        {event.description && (
          <div className="bg-white rounded-3xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Sobre o Evento
            </h3>
            <div
              className="text-gray-700 leading-relaxed whitespace-pre-line style-from-html"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          </div>
        )}

        {/* Company Info */}
        {event.company && event.company.name && (
          <div className="bg-white rounded-3xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Organização
            </h3>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <BuildingStorefrontIcon className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {event.company.name}
                </p>
                {event.company.city && (
                  <p className="text-sm text-gray-500">
                    {event.company.city}
                    {event.company.state ? ` - ${event.company.state}` : ""}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          <Link
            href="/eventos"
            className="flex items-center justify-center gap-2 w-full py-4 bg-mygreen text-white rounded-2xl font-semibold hover:bg-mygreen/90 transition-colors shadow-lg"
          >
            <CalendarIcon className="w-5 h-5" />
            Ver Outros Eventos
          </Link>
          <Link
            href="/contato"
            className="flex items-center justify-center gap-2 w-full py-4 border-2 border-mygreen text-mygreen rounded-2xl font-semibold hover:bg-mygreen/10 transition-colors"
          >
            Quero Divulgar Meu Evento
          </Link>
        </div>
      </div>

      {/* Floating Share FAB */}
      <button
        onClick={handleShare}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-mygreen text-white shadow-lg flex items-center justify-center hover:bg-mygreen/90 transition-all active:scale-95 z-50"
      >
        <ShareIcon className="w-6 h-6" />
      </button>
    </div>
  );
}
